import { useState, useEffect } from 'react'
import { Users, Monitor, CreditCard, TrendingUp, ArrowUpRight, AlertTriangle } from 'lucide-react'
import { getAdminStats, getAdminActivity } from '../../lib/api'
import { formatCurrency, formatTimeAgo } from '../../lib/utils'

export default function AdminDashboard() {
  const [stats, setStats] = useState(null)
  const [activity, setActivity] = useState([])
  const [loading, setLoading] = useState(true)
  const [statsError, setStatsError] = useState(false)

  useEffect(() => {
    getAdminStats().then(data => { setStats(data); setLoading(false) })
      .catch(() => { setStatsError(true); setLoading(false) })
    getAdminActivity().then(setActivity).catch(() => setActivity([]))
  }, [])

  if (loading) return <div className="p-8 flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-flight-cyan border-t-transparent rounded-full animate-spin" /></div>

  if (statsError || !stats) return (
    <div className="p-8 flex flex-col items-center justify-center h-full text-slate-500 gap-3">
      <AlertTriangle className="w-8 h-8" />
      <p className="text-sm">Couldn't load dashboard stats</p>
    </div>
  )

  const totalUsersForChart = (stats.planDistribution || []).reduce((a, p) => a + p.count, 0) || 1
  const planColors = { Free: 'bg-slate-600', Pro: 'bg-flight-cyan', Business: 'bg-flight-amber', Enterprise: 'bg-flight-violet' }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 text-sm">Overview of your FlightWall business</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={<Users className="w-5 h-5" />} label="Total Users" value={stats.totalUsers} change={+12} color="cyan" />
        <StatCard icon={<Monitor className="w-5 h-5" />} label="Active Displays" value={stats.activeDisplays} change={+8} color="emerald" />
        <StatCard icon={<CreditCard className="w-5 h-5" />} label="Monthly Revenue" value={formatCurrency(stats.mrr)} change={+24} color="amber" />
        <StatCard icon={<TrendingUp className="w-5 h-5" />} label="Total Revenue" value={formatCurrency(stats.totalRevenue)} change={+18} color="violet" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-panel p-6">
          <h2 className="text-lg font-bold text-white mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {activity.length === 0 && <div className="text-slate-500 text-sm py-4">No activity yet</div>}
            {activity.map((a, i) => (
              <ActivityItem
                key={i}
                action={a.action}
                user={a.email}
                time={formatTimeAgo(a.time)}
                value={a.value ? `+${formatCurrency(a.value)}` : ''}
              />
            ))}
          </div>
        </div>
        <div className="glass-panel p-6">
          <h2 className="text-lg font-bold text-white mb-4">Plan Distribution</h2>
          <div className="space-y-4">
            {(stats.planDistribution || []).map(p => (
              <PlanBar key={p.name} name={p.name} count={p.count} total={totalUsersForChart} color={planColors[p.name] || 'bg-slate-600'} />
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-slate-800">
            <div className="flex items-center justify-between text-sm"><span className="text-slate-400">Churn Rate</span><span className="text-emerald-400 font-mono">{stats.churnRate}%</span></div>
            <div className="flex items-center justify-between text-sm mt-2"><span className="text-slate-400">Avg Displays / User</span><span className="text-white font-mono">{stats.avgDisplaysPerUser}</span></div>
          </div>
        </div>
      </div>
    </div>
  )
}

function StatCard({ icon, label, value, change, color }) {
  const colors = { cyan: 'bg-flight-cyan/10 text-flight-cyan', emerald: 'bg-flight-emerald/10 text-flight-emerald', amber: 'bg-flight-amber/10 text-flight-amber', violet: 'bg-flight-violet/10 text-flight-violet' }
  return (
    <div className="glass-panel p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>{icon}</div>
        <div className="flex items-center gap-1 text-xs text-emerald-400"><ArrowUpRight className="w-3 h-3" />{Math.abs(change)}%</div>
      </div>
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-slate-400 text-xs">{label}</div>
    </div>
  )
}

function ActivityItem({ action, user, time, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-800/50 last:border-0">
      <div><div className="text-white text-sm">{action}</div><div className="text-slate-500 text-xs">{user} • {time}</div></div>
      {value && <div className="text-emerald-400 text-sm font-mono">{value}</div>}
    </div>
  )
}

function PlanBar({ name, count, total, color }) {
  const pct = (count / total) * 100
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1"><span className="text-slate-300">{name}</span><span className="text-slate-400">{count} users</span></div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden"><div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} /></div>
    </div>
  )
}
